import { ChangeEvent, Fragment, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { Tax, TaxConfigScope } from "models/taxes"
import { css, cx } from "@emotion/css"

import { Autocomplete } from "@material-ui/lab"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { TextInput } from "components/inputs/TextInput"
import { createTax } from "api/taxes"
import { globalState } from "../index"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useMutation } from "react-query"
import { useSendFeedBack } from "components/FeedbackSnackbar"

type Props = {
  onClose: VoidFunction
  isLoading: boolean
  data?: Tax[]
  configScope?: TaxConfigScope
  configOwnerId?: string
}

export const AddNewTax = (props: Props) => {
  const { t } = useI18n()
  const { onClose, isLoading, configOwnerId, configScope } = props
  const [state, setState] = useState<Partial<Tax> | undefined>({})
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const { hasAuthority } = useAuthenticated()
  const feedback = useSendFeedBack()

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const { value, name } = e.target
    setState((s) => ({ ...s, [name]: value }))
    setErrors(undefined)
  }

  // ** create new tax
  const [create] = useMutation(createTax, {
    onSuccess: (d: Tax) => {
      globalState.addRow(d)
      setErrors(undefined)
      onClose()
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      feedback(err.message, "error")
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const taxUsageMC = { label: t("MC"), value: "MC" }
  const taxUsageAC = { label: t("AC"), value: "AC" }

  const taxUsageOptions =
    state?.invoiceScope === "CLIENT" ? [taxUsageAC, taxUsageMC] : [taxUsageMC]

  const invoiceScopeOptions = [
    { label: t("tax.invoiceScope.client"), value: "CLIENT" },
    { label: t("tax.invoiceScope.vendor"), value: "VENDOR" },
  ]

  const handleSelectChange =
    (name: string) => (_event: ChangeEvent<{}>, value: string | null) => {
      if (name === "invoiceScope") {
        setState((s) => ({ ...s, taxUsage: undefined }))
      }
      setState((s) => ({ ...s, [name]: value }))
    }

  const handleCreate = () => {
    const pld = {
      configOwnerId,
      configScope,
      label: state?.label,
      amount: 0,
      taxUsage: state?.taxUsage,
      invoiceScope: state?.invoiceScope,
    }
    create(pld)
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <TextInput
          id="label"
          name="label"
          size="medium"
          color="primary"
          onChange={handleChange}
          label={t(`create.tax.input.label`)}
          className={cx("BodyBody-2BlackRegular", styles.textInput)}
          error={!!errors && !!errors["label"]}
          disabled={!hasAuthority(["AUTH_CREATE_TAX"])}
        />
        <Autocomplete<string>
          placeholder={t("create.tax.input.invoiceScope")}
          options={invoiceScopeOptions?.map((is) => is.value)}
          value={state?.invoiceScope}
          onChange={handleSelectChange("invoiceScope")}
          className={styles.input}
          getOptionLabel={(value) =>
            invoiceScopeOptions?.find((v) => v?.value === value)?.label || ""
          }
          popupIcon={<DropdownIcon />}
          renderInput={(prms) => (
            <TextInput
              {...prms}
              name="invoiceScope"
              size="medium"
              margin="normal"
              label={t("create.tax.input.invoiceScope")}
              placeholder={t("create.tax.input.invoiceScope")}
              error={!!errors && !!errors["invoiceScope"]}
              errorMessage={!!errors ? errors["invoiceScope"] : ""}
              InputProps={{
                ...prms.InputProps,
                className: cx("BodyBody-2BlackRegular", styles.selectInput),
                endAdornment: (
                  <Fragment>{prms.InputProps.endAdornment}</Fragment>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          placeholder={t("create.tax.input.taxUsage")}
          options={taxUsageOptions?.map((tu) => tu.value)}
          value={state?.taxUsage || ""}
          onChange={handleSelectChange("taxUsage")}
          className={styles.input}
          getOptionLabel={(value) =>
            taxUsageOptions?.find((v) => v?.value === value)?.label || ""
          }
          popupIcon={<DropdownIcon />}
          renderInput={(prms) => (
            <TextInput
              {...prms}
              name="taxUsage"
              size="medium"
              margin="normal"
              label={t("create.tax.input.taxUsage")}
              placeholder={t("create.tax.input.taxUsage")}
              error={!!errors && !!errors["taxUsage"]}
              errorMessage={!!errors ? errors["taxUsage"] : ""}
              InputProps={{
                ...prms.InputProps,
                className: cx("BodyBody-2BlackRegular", styles.selectInput),
                endAdornment: (
                  <Fragment>{prms.InputProps.endAdornment}</Fragment>
                ),
              }}
            />
          )}
        />
      </div>
      <ButtonGroup className={styles.actions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          disabled={isLoading}
        >
          {t("warning.modal.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={!state?.label || !state?.invoiceScope || !state?.taxUsage}
        >
          {t("button.add")}
        </Button>
      </ButtonGroup>
    </div>
  )
}

const styles = {
  wrapper: css`
    border-radius: 4px;
    box-shadow: 1px 6px 10px 0 var(--disabled-grey);
    border: solid 1px var(--lighter-grey);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-s);
    margin-bottom: var(--spacing-m);
  `,
  content: css`
    display: flex;
    align-items: center;
  `,
  input: css`
    width: 250px;
    margin-right: var(--spacing-s);
  `,
  textInput: css`
    width: 250px;
    margin-right: var(--spacing-s);
    margin-top: var(--spacing-xs);
  `,
  selectInput: css`
    width: 250px;
    padding-right: var(--spacing-xxl-4);
    .MuiFormControl-marginNormal {
      margin: 0;
    }
    .MuiAutocomplete-endAdornment {
      right: 9px;
    }
  `,
  select: css`
    width: 250px;
    min-height: 48px;
    margin-right: var(--spacing-s);
  `,
  actions: css`
    display: flex;
    margin-top: var(--spacing-xs);
  `,
}
